import _ from 'underscore';
import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { formatPercentOneDecimal, CHANNEL_TYPES } from '@bingads-webui-campaign/recommendation-core';
import { HighchartReactWrapper } from '@bingads-webui-react/highchart-react-wrapper';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { TooltipHost, DirectionalHint, TooltipDelay, Link } from '@fluentui/react';

const getChartOptions = (Available, Gained, Dismissed) => ({
  chart: {
    width: 50,
    height: 17,
    margin: [0, 0, 0, 0],
    type: 'bar',
  },
  title: {
    text: null,
  },
  xAxis: {
    visible: false,
  },
  yAxis: {
    visible: false,
    tickPositions: [-2, 102],
  },
  legend: {
    enabled: false,
  },
  credits: {
    enabled: false,
  },
  tooltip: {
    enabled: false,
    formatter: null,
  },
  plotOptions: {
    bar: {
      animation: false,
      pointWidth: 16,
      borderColor: '#0078D4',
    },
    series: {
      cursor: 'pointer',
      point: {
        events: {
          click: null,
        },
      },
      stacking: 'normal',
      states: {
        hover: {
          enabled: false,
        },
        inactive: {
          enabled: false,
        },
      },
    },
  },
  series: [
    {
      data: [Available * 100],
      color: '#FFFFFF',
    },
    {
      data: [Dismissed * 100],
      color: '#ABDBFF',
    },
    {
      data: [Gained * 100],
      color: '#0078D4',
    },
  ],
});

const hostStyles = {
  root: {
    display: 'inline-block',
  },
};
const calloutProps = { gapSpace: 7 };
const tooltipProps = {
  maxWidth: '231px',
  styles: {
    root: {
      padding: '8px 10px',
    },
  },
  delay: TooltipDelay.long,
};
const viewRecommendationStyle = { marginTop: '10px' };

export function OptimizationScoreItem(props) {
  const { scope } = useContext(ScopeContext);
  const { osData, i18n } = props;

  const navigate = useCallback((event) => {
    if (event && (event.button !== 0 ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.metaKey)) {
      return true;
    }

    event.preventDefault();
    scope.set(_.extend({ recsrc: CHANNEL_TYPES.CAMPAIGN_SUMMARY_BAR }, scope.values));
    scope.save('/campaign/vnext/recommendations/All');
    return false;
  }, [scope]);

  const calloutContent = useCallback(() => {
    const url = `/campaign/vnext/recommendations/All?${
      scope.stringify(_.extend(
        {
          recsrc: CHANNEL_TYPES.CAMPAIGN_SUMMARY_BAR,
        },
        scope.values
      ))}`;

    return (
      <div className="optimization-score-tooltip">
        {i18n.getString(_TL_('View relevant recommendations to improve your optimization score'))}
        <div style={viewRecommendationStyle}>
          <Link
            href={url}
            onClick={navigate}
          >
            {i18n.getString(_TL_('View recommendations'))}
          </Link>
        </div>
      </div>
    );
  }, [i18n, navigate, scope]);

  const getScore = useCallback(() => {
    if (osData && osData.OverallOptimizationScoreBar) {
      const { OverallOptimizationScoreBar } = osData;
      const { Available, Gained, Dismissed } = OverallOptimizationScoreBar;
      return (
        <React.Fragment>
          <div className="summary-bar-overall-score-value">{formatPercentOneDecimal(Gained + Dismissed, i18n)}</div>
          <div className="summary-bar-overall-score-chart"><HighchartReactWrapper options={getChartOptions(Available, Gained, Dismissed)} /></div>
        </React.Fragment>
      );
    }
    return (
      <div className="summary-bar-overall-score-none" >-</div>
    );
  }, [i18n, osData]);

  return (
    <div className="summary-bar-overall-score-container">
      <TooltipHost
        content={calloutContent()}
        directionalHint={DirectionalHint.bottomCenter}
        styles={hostStyles}
        calloutProps={calloutProps}
        tooltipProps={tooltipProps}
      >
        <span className="entity-label">{i18n.getString(_TL_('Optimization score'))}</span>
        <div className="summary-bar-overall-score" >{getScore()}</div>
      </TooltipHost>
    </div>
  );
}

OptimizationScoreItem.propTypes = {
  osData: PropTypes.shape({
    OverallOptimizationScoreBar: PropTypes.shape({
      Available: PropTypes.number,
      Gained: PropTypes.number,
      Dismissed: PropTypes.number,
    }).isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};
